import { useState, WheelEvent } from "react";
import { Skeleton } from "@mui/material";
import { useParams } from "react-router";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  ChartOptions,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useSnapshot } from "valtio";

import {
  calculatePercentiles,
  getAgeFromMetadata,
  RegressionModel,
} from "lib/client/normative";
import useMarkers, {
  parseQuantitativeDataFromSR,
} from "shared/hooks/useMarkers";
import normativeGraphState from "stores/normativeGraph";
import useLongitudinal from "shared/hooks/useLongitudinal";
import { useTranslate } from "ra-core";
import { YYYYMMDDtoDate } from "shared/utils/date";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

export const percentileColors = [
  [208, 49, 45],
  [236, 151, 135],
  [231, 209, 38],
];

export const quantificationColors = {
  currentStudy: "#005678",
  other: "black",
} as const;

function NormativeGraph() {
  const { study_id } = useParams();
  const snap = useSnapshot(normativeGraphState);
  const [zoom, setZoom] = useState(10);
  const t = useTranslate();

  const {
    regressionModels,
    isRegressionModelLoading,
    populatedMarkers,
    naturalizedDatasets,
  } = useMarkers(study_id);

  const longitudinal = useLongitudinal();
  const { studiesInstanceMetadata, timepoints } = longitudinal as any;
  const isDone = longitudinal?.studiesIsDone ?? longitudinal?.isDone ?? true;

  const regressionModel =
    regressionModels?.[snap.rid] ||
    (regressionModels as any)?.models?.[snap.rid];

  const currentMarker = populatedMarkers?.find(
    (marker) => marker.code === snap.rid,
  );
  const markersPercentile = currentMarker?.percentiles;

  const currentMeta = naturalizedDatasets?.[0];
  const currentAge = currentMeta
    ? getAgeFromMetadata(currentMeta, true) ?? getAgeFromMetadata(currentMeta)
    : null;

  if (
    !regressionModel ||
    isRegressionModelLoading ||
    (!isDone && !populatedMarkers?.length)
  ) {
    return <Skeleton className="h-full w-full" />;
  }

  let quantificationPoints: {
    age: number;
    icv: number;
    currentStudy: boolean;
  }[] = [];

  if (timepoints && timepoints.length > 0) {
    const birthDateStr = currentMeta?.PatientBirthDate;
    const birthDate = birthDateStr ? YYYYMMDDtoDate(birthDateStr) : null;

    quantificationPoints = timepoints
      .map((tp: any) => {
        const icv = tp.quantification?.find(
          (quant: any) =>
            quant[0] === snap.rid &&
            (quant[2] === "%" || quant[2] === "Percent"),
        )?.[1];

        let age = tp.age;
        if (!age && tp.studyDate && birthDate) {
          const studyDate = YYYYMMDDtoDate(tp.studyDate.replace("ses-", ""));
          const diffTime = studyDate.getTime() - birthDate.getTime();
          age = diffTime / (1000 * 60 * 60 * 24 * 365.25);
        }

        const isCurrent =
          tp.studyDate === study_id ||
          tp.studyDate?.replace("ses-", "") === study_id?.replace("ses-", "");

        return {
          age: age
            ? Number(age.toFixed(2))
            : isCurrent && currentAge
            ? Number(currentAge.toFixed(2))
            : 0,
          icv:
            icv !== undefined
              ? Number(icv)
              : isCurrent
              ? currentMarker?.icv
              : undefined,
          currentStudy: isCurrent,
        };
      })
      .filter((p: any) => p.icv !== undefined && p.age > 0);
  } else if (studiesInstanceMetadata && studiesInstanceMetadata.length > 0) {
    quantificationPoints = studiesInstanceMetadata
      ?.filter((instances: any) => instances?.[0])
      .map((instances: any) => {
        const studyAge = Number(
          (
            getAgeFromMetadata(instances?.[0], true) ??
            getAgeFromMetadata(instances?.[0])
          )?.toFixed(2),
        );
        const icv = parseQuantitativeDataFromSR(instances)?.find(
          (quant: any) => quant[0] === snap.rid && quant[2] === "%",
        )?.[1];

        return {
          age: studyAge,
          icv: icv,
          currentStudy: instances?.[0]?.StudyInstanceUID === study_id,
        };
      })
      .filter((p: any) => p.icv !== undefined && !isNaN(p.age));
  }

  if (currentMarker && currentMarker.icv !== undefined) {
    const hasCurrent = quantificationPoints.some((p) => p.currentStudy);
    if (!hasCurrent && currentAge) {
      quantificationPoints.push({
        age: Number(currentAge.toFixed(2)),
        icv: currentMarker.icv,
        currentStudy: true,
      });
    }
  }

  quantificationPoints.sort((a, b) => a.age - b.age);

  const data = computeNormativeData(
    regressionModel,
    quantificationPoints,
    markersPercentile,
  );

  const ages = quantificationPoints.map(({ age }) => age).filter((a) => a > 0);
  const minAge = ages.length > 0 ? Math.min(...ages) : 28;
  const maxAge = ages.length > 0 ? Math.max(...ages) : 90;

  const options: ChartOptions<"line"> = {
    responsive: true,
    elements: {
      point: {
        pointStyle: "circle",
        backgroundColor: quantificationColors.currentStudy,
      },
    },
    scales: {
      xAxis: {
        type: "linear",
        min: minAge - zoom,
        max: maxAge + zoom,
        title: {
          display: true,
          text: t("modes.normative_graph.age"),
        },
      },
      y: {
        title: {
          display: true,
          text: t("modes.normative_graph.volume"),
        },
      },
    },
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: `${t("modes.normative_graph")} : ${snap.rid
          .split(":")
          .map((rid) => t(rid))
          .join(" : ")}`,
      },
    },
  };

  function handleWheelEvent(e: WheelEvent<HTMLDivElement>) {
    if (e.deltaY >= 0 && zoom < 30) {
      setZoom((prev) => prev + 5);
    } else if (e.deltaY < 0 && zoom > 5) {
      setZoom((prev) => prev - 5);
    }
  }

  return (
    <div
      data-testid="viewer-mode-normative-graph"
      className="h-full"
      onWheel={handleWheelEvent}
    >
      <Line options={options} data={data} />
    </div>
  );
}

function computeNormativeData(
  regressionModel: RegressionModel,
  quantification: {
    age: number;
    icv: number;
    currentStudy: boolean;
  }[] = [],
  percToDisplay: number[] = [0.01, 0.05, 0.25, 0.5],
) {
  const min = 0;
  const max = 200;
  const normMin = regressionModel?.min ?? 0;
  const normMax = regressionModel?.max ?? 90;

  const applyStyle = (ctx: any, value: any) =>
    ctx.p0.parsed.x < normMin || ctx.p0.parsed.x > normMax ? value : undefined;

  const data: {
    age: number;
    percentiles: ReturnType<typeof calculatePercentiles>;
  }[] = [];

  for (let age = min; age < max; age++) {
    data.push({
      age,
      percentiles: calculatePercentiles(regressionModel, age),
    });
  }

  const ds = percToDisplay.map((perc, i) => {
    let fill: string | number | undefined = i;
    if (perc === 0.5) fill = undefined;
    else if (i === 0 && perc > 0.5) fill = "end";
    else if (i === 0 && perc < 0.5) fill = "start";

    const color =
      perc === 0.5
        ? "white"
        : `rgb(${percentileColors[i]?.join(",") || "200,200,200"})`;

    return {
      label: `P${perc.toFixed(2).substring(2)}`,
      data: data.map(({ age, percentiles }) => ({
        x: age,
        y: percentiles[String(perc)],
      })),
      borderColor: color,
      backgroundColor: color,
      segment: {
        backgroundColor: (ctx: any) =>
          applyStyle(
            ctx,
            `rgb(${percentileColors[i]?.join(",") || "200,200,200"}, 0.1)`,
          ),
        borderDash: (ctx: any) => applyStyle(ctx, [3, 3]),
      },
      fill,
    };
  });

  const datasets = [
    {
      label: "Quantification",
      data: quantification.map(({ age, icv }) => ({ x: age, y: icv })),
      pointBackgroundColor: quantification.map(({ currentStudy }) =>
        currentStudy
          ? quantificationColors.currentStudy
          : quantificationColors.other,
      ),
      pointBorderColor: quantification.map(({ currentStudy }) =>
        currentStudy
          ? quantificationColors.currentStudy
          : quantificationColors.other,
      ),
      borderColor: quantificationColors.other,
      backgroundColor: quantificationColors.other,
      pointStyle: "circle",
      pointRadius: 6,
    },
    ...ds,
  ];

  return { datasets };
}

export default NormativeGraph;
